import type {
  UsageCapabilityName,
  UsageLimit,
  UsageLimitName,
  UsagePlanCapabilities,
  UsageQueuePriorityCapability,
} from "@/features/auth/api/usage";

export const USAGE_QUERY_KEY = ["usage"] as const;

export const USAGE_LIMIT_NAMES: Record<UsageLimitName, string> = {
  dailyPrompts: "Queries per day",
  dailyMaps: "Maps per day",
  workspaceStorage: "Document storage",
  evidenceApiPerMinute: "Evidence API",
};

export const DAILY_LIMIT_NAMES: UsageLimitName[] = ["dailyPrompts", "dailyMaps"];

export const CAPABILITY_NAMES: Record<UsageCapabilityName, string> = {
  heavySearchStartsPerMinute: "Searches per minute",
  heavyGrepStartsPerMinute: "Corpus-wide greps per minute",
  grepMaxResults: "Grep result cap",
};

export const CAPABILITY_ORDER: UsageCapabilityName[] = [
  "heavySearchStartsPerMinute",
  "heavyGrepStartsPerMinute",
  "grepMaxResults",
];

/** "50 per minute" for rate capabilities, "5,000 results" for caps.
 * Shared-pool values get a "(shared)" suffix (one combined pool). */
export const formatCapability = (
  name: UsageCapabilityName,
  total: number,
  isSharedPool = false,
): string => {
  const formatted = total.toLocaleString();
  const suffix = isSharedPool ? " (shared)" : "";
  if (name === "grepMaxResults") return `${formatted} results`;
  return `${formatted} per minute${suffix}`;
};

/** Footnote for shared-pool rate rows. */
export const SHARED_POOL_NOTE =
  "Searches and corpus-wide greps draw from one combined pool";

/** "High priority" (tier 1) vs "Standard" (tier 2+). */
export const formatQueuePriority = (priority: UsageQueuePriorityCapability): string =>
  priority.tier <= 1 ? "High priority" : "Standard";

export const hasQueuePriority = (
  capabilities: UsagePlanCapabilities | null | undefined,
): capabilities is UsagePlanCapabilities =>
  Boolean(capabilities?.heavyQueuePriority);

export const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

export type LimitStatus = {
  isAtLimit: boolean;
  isApproachingLimit: boolean;
};

export const limitStatus = (
  limit: UsageLimit | undefined,
): LimitStatus => {
  if (!limit || limit.isUnlimited || limit.total == null || limit.total <= 0) {
    return { isAtLimit: false, isApproachingLimit: false };
  }
  const atLimit = limit.usage >= limit.total;
  const fraction = limit.usage / limit.total;
  return {
    isAtLimit: atLimit,
    isApproachingLimit: !atLimit && fraction >= 0.75,
  };
};
